import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const BUNDLES = [
  {
    id: 'EB-33230',
    issued: 'Mar 21, 2025 · 08:55',
    facility: 'PNC-SPV-92314 · Whole loan',
    status: 'Valid',
    statusTone: 'good',
    originator: 'Originator',
    asset: 'A-92834 / Loan-L-92189',
    bundleType: 'Admission + Transfer records',
    machineTrust: 'Valid',
    signatureMatch: 'Strong (0.94)',
    admissionReport: 'PASS / Validated',
    evidenceReport: 'Complete',
    importSource: 'System upload (SFTP)',
  },
  {
    id: 'EB-18802-01',
    issued: 'Mar 20, 2025 · 14:22',
    facility: 'UMB Line A · Participation',
    status: 'Pending provider action',
    statusTone: 'warn',
    originator: 'UMB Originator',
    asset: 'A-18802 / Loan-L-18802',
    bundleType: 'Admission only',
    machineTrust: 'Valid',
    signatureMatch: 'Moderate (0.81)',
    admissionReport: 'PASS',
    evidenceReport: 'Partial — 1 doc outstanding',
    importSource: 'Portal upload',
  },
  {
    id: 'EB-39213-91',
    issued: 'Feb 12, 2025 · 11:03',
    facility: 'UMB Line A · Archived SPV',
    status: 'Superseded',
    statusTone: 'bad',
    originator: 'UMB Originator',
    asset: 'A-93284 / Loan-L-92189',
    bundleType: 'Admission + servicing trail',
    machineTrust: 'Historical',
    signatureMatch: 'Strong (0.96)',
    admissionReport: 'PASS (archived)',
    evidenceReport: 'Complete',
    importSource: 'System upload (SFTP)',
  },
];

function statusPill(tone) {
  if (tone === 'good')
    return 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  if (tone === 'warn') return 'bg-amber-50 text-amber-900 ring-amber-200';
  return 'bg-evault-surface text-evault-muted ring-evault-border';
}

export function CustodianEvidenceBundlesPage() {
  const [selectedId, setSelectedId] = useState(BUNDLES[0].id);
  const selected = useMemo(
    () => BUNDLES.find((b) => b.id === selectedId) ?? BUNDLES[0],
    [selectedId],
  );
  const assetSlug = selected.asset.split('/')[0].trim();

  return (
    <div className="page-stack">
      <header className="flex flex-col gap-3 border-b border-evault-border pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-evault-navy">
            Evidence bundles
          </h1>
          <p className="mt-1 max-w-2xl text-xs text-evault-muted">
            Evidence bundle register — facility context, validation posture, and
            import provenance.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy shadow-sm"
          >
            Generate
          </button>
          <button
            type="button"
            className="rounded-full bg-evault-navy px-3 py-1 text-[11px] font-semibold text-white shadow-sm"
          >
            Export
          </button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Bundle count
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">143</p>
          <p className="mt-1 text-[10px] text-evault-muted">+5 today</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Assets today
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">19</p>
          <p className="mt-1 text-[10px] text-evault-muted">by cust. ops</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Pending actions
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">5</p>
          <p className="mt-1 text-[10px] text-evault-gold">in provider record</p>
        </div>
        <div className="card-shell p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Superseded bundles
          </p>
          <p className="mt-1 text-2xl font-semibold text-evault-navy">21</p>
          <p className="mt-1 text-[10px] text-evault-muted">history</p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
        <div className="table-shell overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-[11px]">
            <thead className="bg-evault-surface text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              <tr>
                <th className="px-3 py-2">Bundle ID</th>
                <th className="px-3 py-2">Issued</th>
                <th className="px-3 py-2">Facility context</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Originator</th>
              </tr>
            </thead>
            <tbody>
              {BUNDLES.map((b) => {
                const active = b.id === selected.id;
                return (
                  <tr
                    key={b.id}
                    className={`cursor-pointer border-t border-evault-border ${
                      active ? 'bg-evault-surface' : 'hover:bg-evault-surface/60'
                    }`}
                    onClick={() => setSelectedId(b.id)}
                  >
                    <td className="px-3 py-2 font-semibold text-evault-navy">
                      {b.id}
                    </td>
                    <td className="px-3 py-2 text-evault-muted">{b.issued}</td>
                    <td className="px-3 py-2 text-evault-navy">{b.facility}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${statusPill(b.statusTone)}`}
                      >
                        {b.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-evault-muted">{b.originator}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <aside className="card-shell flex flex-col gap-3 p-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              Selected evidence bundle
            </p>
            <p className="mt-1 text-sm font-semibold text-evault-navy">
              Bundle ID: {selected.id}
            </p>
          </div>
          <dl className="space-y-2 text-[11px]">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Asset
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.asset}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Bundle type
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.bundleType}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Machine trust state
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.machineTrust}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Signature match
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.signatureMatch}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Admission report
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.admissionReport}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Evidence report
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.evidenceReport}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                Import source
              </dt>
              <dd className="mt-0.5 text-evault-navy">{selected.importSource}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2 border-t border-evault-border pt-3">
            <button
              type="button"
              className="rounded-full bg-evault-teal-deep px-3 py-1 text-[11px] font-semibold text-white"
            >
              View PDF
            </button>
            <button
              type="button"
              className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy"
            >
              Report
            </button>
            <button
              type="button"
              className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy"
            >
              Re-scan
            </button>
          </div>
          <div className="border-t border-evault-border pt-3 text-[11px]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
              Related
            </p>
            <ul className="mt-2 space-y-1.5 text-evault-teal-deep">
              <li>
                <Link to="/custodian/reports" className="hover:underline">
                  Validation PDF
                </Link>
              </li>
              <li>
                <Link to="/custodian/release-copy" className="hover:underline">
                  Linked package (SP)
                </Link>
              </li>
              <li>
                <Link
                  to={`/custodian/assets/${assetSlug}`}
                  className="hover:underline"
                >
                  Assets in bundle
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
